import { NewbuildingCarousel } from '@cian/frontend-newbuilding-carousel-component';
import { ActionAfterViewObserver, useBooleanState } from '@cian/react-utils';

import { type FC, useCallback, useState } from 'react';

import { type MainCarouselBanner } from 'shared/types/mainCarousel';

import { Layout } from '../Layout';
import { NoImage } from '../NoImage';
import { type AnimationDuration, SliderProgressBar } from '../SliderProgressBar';

import { firstPaintOptions, mediaSettings } from './config';
import { Slide } from './internal/components/Slide';

import styles from './Carousel.css';

export type CarouselProps = {
  banners: MainCarouselBanner[];
  testId?: string;
  interval: AnimationDuration;
  onView: () => void;
  onDotClick: (index: number) => void;
  onSelectApartmentClick: (newbuildingId: number, index: number) => void;
};

export const Carousel: FC<CarouselProps> = ({
  banners,
  testId,
  interval,
  onView,
  onDotClick,
  onSelectApartmentClick,
}) => {
  const [currentSlide, setCurrentSlide] = useState(1);
  const { state: paused, setTrue: setPaused, setFalse: setNotPaused } = useBooleanState(false);

  const handleAnimationEnd = useCallback(
    () => setCurrentSlide(prev => (prev < banners.length ? prev + 1 : 1)),
    [banners.length],
  );

  if (!banners.length) {
    return (
      <ActionAfterViewObserver
        onInViewport={({ subscribeState }) => {
          subscribeState.setSubscribeUnavailable();
          onView();
        }}
      >
        {({ elementRef }) => (
          <section className={styles['carousel']} ref={elementRef}>
            <NoImage />
          </section>
        )}
      </ActionAfterViewObserver>
    );
  }

  return (
    <ActionAfterViewObserver
      onInViewport={({ subscribeState }) => {
        subscribeState.setSubscribeUnavailable();
        onView();
      }}
    >
      {({ elementRef }) => (
        <section className={styles['carousel']} data-testid={testId} ref={elementRef}>
          <NewbuildingCarousel
            defaultCurrentSlide={currentSlide}
            firstPaintOptions={firstPaintOptions}
            media={mediaSettings}
            slides={banners.map((banner, index) => (
              <Slide
                active={index === currentSlide - 1}
                badges={banner.badges}
                disclaimer={banner.disclaimer}
                hasCommercialProperty={banner.hasCommercialProperty}
                imageUrl={banner.imageUrl}
                key={banner.id}
                label={banner.label}
                logoUrl={banner.logoUrl}
                newbuildingId={banner.id}
                publicUrl={banner.publicUrl}
                testId={`main-carousel-slide-${banner.id}`}
                title={banner.title}
                transports={banner.transports}
                video={banner.video}
                onSelectApartmentClick={() => onSelectApartmentClick(banner.id, index)}
                onTickerMouseOver={setPaused}
                onTickerMouseLeave={setNotPaused}
              />
            ))}
            onSlideChange={({ currentSlide: slideIndex }) => setCurrentSlide(slideIndex)}
          />

          {banners.length > 1 && (
            <div className={styles['progress-bar']}>
              <Layout>
                <div className={styles['progress-bar__wrapper']}>
                  <SliderProgressBar
                    activeIndex={currentSlide - 1}
                    count={banners.length}
                    duration={interval}
                    paused={paused}
                    onAnimationEnd={handleAnimationEnd}
                    onDotClick={index => {
                      onDotClick(index);
                      setCurrentSlide(index + 1);
                    }}
                    onProgressBarHover={setPaused}
                    onProgressBarLeave={setNotPaused}
                  />
                </div>
              </Layout>
            </div>
          )}
        </section>
      )}
    </ActionAfterViewObserver>
  );
};
